import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { ParticipacionEdicionActiva } from '../lib/services/restauranteParticipacionEdicion';

type Props = {
  loading: boolean;
  /** Resultado de la consulta; `null` si aún no aplica o falló silenciosamente. */
  data: ParticipacionEdicionActiva | null;
  /** Ir a `/votar/:id` con la propuesta pública resuelta. */
  onVotar?: () => void;
};

export function RestauranteEdicionActivaParticipacionCard({ loading, data, onVotar }: Props) {
  if (loading) {
    return (
      <View style={[styles.box, styles.boxLoading]} accessibilityRole="text">
        <ActivityIndicator size="small" color="#6b7280" />
        <Text style={styles.loadingText}>Comprobando si participa en la edición activa…</Text>
      </View>
    );
  }

  if (!data) return null;

  if (data.participa) {
    const puedeVotar = Boolean(data.propuestaIdVotacion && onVotar);
    return (
      <View style={[styles.box, styles.boxOk, styles.boxOkStack]} accessibilityRole="summary">
        <View style={styles.boxOkHeaderRow}>
          <Ionicons name="checkmark-circle" size={24} color="#15803d" />
          <View style={styles.textCol}>
            <Text style={styles.titleOk}>Sí participa en la edición en curso</Text>
            <Text style={styles.body}>
              {data.edicionNombre
                ? `Este restaurante está inscrito en «${data.edicionNombre}», la edición que está activa ahora.`
                : 'Este restaurante está inscrito en la edición que está activa en este momento.'}
            </Text>
          </View>
        </View>
        {puedeVotar ? (
          <TouchableOpacity style={styles.votarBtn} onPress={onVotar} activeOpacity={0.88} accessibilityRole="button">
            <Ionicons name="checkbox-outline" size={22} color="#ffffff" />
            <Text style={styles.votarBtnText}>Votar</Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.sinPropuestaHint}>
            Aún no hay una propuesta pública asociada para abrir la votación desde aquí.
          </Text>
        )}
      </View>
    );
  }

  return (
    <View style={[styles.box, styles.boxNeutral]} accessibilityRole="summary">
      <Ionicons name="information-circle-outline" size={24} color="#6b7280" />
      <View style={styles.textCol}>
        <Text style={styles.titleNeutral}>No participa en una edición activa</Text>
        <Text style={styles.body}>
          Ahora mismo no figura en ninguna edición en estado «activa». Puede estar asociado a otra fase
          (anunciada, catálogo, etc.) o no estar vinculado a la edición vigente.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  boxLoading: {
    backgroundColor: '#f9fafb',
    borderColor: '#e5e7eb',
    alignItems: 'center',
  },
  boxOk: {
    backgroundColor: '#f0fdf4',
    borderColor: '#bbf7d0',
  },
  boxOkStack: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 14,
  },
  boxOkHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  votarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#A20000',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  votarBtnText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '700',
  },
  sinPropuestaHint: {
    fontSize: 13,
    color: '#166534',
    lineHeight: 18,
    opacity: 0.9,
  },
  boxNeutral: {
    backgroundColor: '#f9fafb',
    borderColor: '#e5e7eb',
  },
  textCol: {
    flex: 1,
    gap: 6,
  },
  titleOk: {
    fontSize: 15,
    fontWeight: '700',
    color: '#166534',
  },
  titleNeutral: {
    fontSize: 15,
    fontWeight: '700',
    color: '#374151',
  },
  body: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  loadingText: {
    flex: 1,
    fontSize: 14,
    color: '#6b7280',
  },
});
